import { ReactElement, useMemo } from 'react';

import { Translate, useFormController } from '@dreryk/react-common';
import { FormController, StrongText, StyledFlex, Text } from '@dreryk/react-components';

import { refundAdvancePaymentTranslationKeys } from '../translations';

import { RefundAmountField } from './fields/RefundAmountField';
import { PriceTextView } from './priceTextView/PriceTextView';
import {
   RefundAdvancePaymentFormBean,
   RefundAdvancePaymentFormController,
   RefundAdvancePaymentFormInitializer,
} from './RefundAdvancePaymentForm.types';
import { amountSectionViewVisitor } from './RefundAdvancePaymentForm.utils';

const t = refundAdvancePaymentTranslationKeys.form.textView;

type RefundAdvancePaymentFormRenderProps = Pick<RefundAdvancePaymentFormController, 'handleSubmit' | 'formState'>;

type RefundAdvancePaymentFormProps = {
   readonly initializer: RefundAdvancePaymentFormInitializer;
   readonly children: (renderProps: RefundAdvancePaymentFormRenderProps) => ReactElement;
};

export const RefundAdvancePaymentForm = (props: RefundAdvancePaymentFormProps): ReactElement => {
   const { initializer, children } = props;
   const { formState, handleSubmit, register, watch } = useFormController<RefundAdvancePaymentFormBean>(
      useMemo(() => initializer(), [initializer])
   );
   const { advancePaymentAmount, totalAmount, refundAmount } = watch();

   const balanceAmount = totalAmount - advancePaymentAmount;
   const afterRefundAmount = totalAmount - advancePaymentAmount + refundAmount;
   const refundAmountGreaterThanAdvancePaymentAmount = refundAmount > advancePaymentAmount;

   return (
      <StyledFlex as={'form'} direction='column' gap={18} noValidate autoComplete={'off'}>
         <StyledFlex gap={38}>
            <StyledFlex direction={'column'} gap={4}>
               <Text testId={'totalAmountLabel'}>
                  <Translate>{t.totalAmount.label}</Translate>
               </Text>
               <Text testId={'advancePaymentAmountLabel'}>
                  <Translate>{t.advancePaymentAmount.label}</Translate>
               </Text>
               {amountSectionViewVisitor(balanceAmount)({
                  forPositive: () => (
                     <StrongText testId={'remainingAmountLabel'}>
                        <Translate>{t.remainingAmount.label}</Translate>
                     </StrongText>
                  ),
                  forNegative: () => (
                     <StrongText testId={'overpaymentAmountLabel'}>
                        <Translate>{t.overpaymentAmount.label}</Translate>
                     </StrongText>
                  ),
               })}
            </StyledFlex>
            <StyledFlex direction={'column'} gap={4}>
               <PriceTextView testId={'totalAmount'} value={totalAmount} />
               <PriceTextView testId={'advancePaymentAmount'} value={advancePaymentAmount} />
               <strong>
                  <PriceTextView testId={'overpaymentAmount'} value={Math.abs(balanceAmount)} />
               </strong>
            </StyledFlex>
         </StyledFlex>

         <FormController controller={register('advancePaymentAmount')} />
         <FormController controller={register('totalAmount')} />
         <RefundAmountField register={register} />

         <StyledFlex gap={8}>
            {amountSectionViewVisitor(Number(afterRefundAmount.toFixed(2)))({
               forPositive: () => (
                  <Text testId={'afterRefundAmountLabel'}>
                     <Translate>{t.afterRefundAmount.label}</Translate>
                  </Text>
               ),
               forNegative: () => (
                  <Text testId={'overpaymentAmountLabel'}>
                     <Translate>{t.overpaymentAmount.label}</Translate>
                  </Text>
               ),
            })}

            {refundAmountGreaterThanAdvancePaymentAmount ? (
               <Text testId={'afterRefundAmount'}>
                  <Translate>{t.afterRefundAmount.emptyPlaceholder}</Translate>
               </Text>
            ) : (
               <PriceTextView testId={'afterRefundAmount'} value={Math.max(0, Math.abs(afterRefundAmount))} />
            )}
         </StyledFlex>

         {children({ formState: formState, handleSubmit: handleSubmit })}
      </StyledFlex>
   );
};
